/* This example requires Tailwind CSS v2.0+ */
import { FC, Fragment, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { cn } from "helpers/classNames";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { ProxyAssignmentTag } from "./ProxyAssignmentTag";
import { PriorityIcon } from "pages/app/uppdrag-list-page/PriorityIcon";
import {
  ModalPrimaryButton,
  ModalSecondaryAction,
} from "pages/app/customer/AddFacility/AddFacilityModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  actionText?: string;
  actionHandler?: () => void;
  title: string;
  actionDisabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  isWide?: boolean;
  isPrettyWide?: boolean;
  closeButtonText?: string;
  // secondaryAction?: () => void;
  hideButtons?: boolean;
  isProxyAssignment?: boolean;
  isPriorityAssignment?: boolean;
  afterClose?: () => void;
  errorTriangle?: boolean;
  isReallyWide?: boolean;
}
const Modal: React.FC<Props> = ({
  isOpen,
  onClose,
  children,
  actionText,
  actionHandler,
  title,
  actionDisabled = false,
  isLoading = false,
  loadingText = "Sparar...",
  isWide = false,
  isPrettyWide = false,
  closeButtonText = "Avbryt",
  // secondaryAction,
  hideButtons = false,
  isProxyAssignment,
  isPriorityAssignment = false,
  afterClose,
  errorTriangle = false,
  isReallyWide = false,
}) => {
  // const cancelButtonRef = useRef(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const stopDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };
  useEffect(() => {
    modalRef?.current?.addEventListener("dragover", stopDrop);
    modalRef?.current?.addEventListener("drop", stopDrop);
    return () => {
      modalRef?.current?.removeEventListener("dragover", stopDrop);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      modalRef?.current?.removeEventListener("drop", stopDrop);
    };
  }, []);

  return (
    <Transition show={isOpen} as={Fragment} afterLeave={afterClose}>
      <Dialog
        as="div"
        className="relative z-10"
        // initialFocus={cancelButtonRef}
        onClose={() => null}
        ref={modalRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-100"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={cn(
                  "relative w-full transform rounded-lg bg-white p-2 pb-4 pt-2 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:pb-2",
                  isWide && "md:max-w-5xl",
                  isPrettyWide && "md:max-w-3xl",
                  isReallyWide && "md:max-w-7xl",
                )}
              >
                <ModalCloseIcon onClick={onClose} />

                <div>
                  <div>
                    <DialogTitle
                      as="h3"
                      className={cn(
                        "flex items-center justify-between p-2 text-lg font-medium leading-6 text-gray-900 sm:p-4",
                        // !!secondaryAction && "flex justify-between",
                        // (!!isProxyAssignment || errorTriangle) &&
                        //   "items-center",
                      )}
                    >
                      {title}
                      {!!isProxyAssignment && (
                        <div className="ml-4">
                          <ProxyAssignmentTag />
                        </div>
                      )}
                      {!!isPriorityAssignment && (
                        <div className="ml-4 flex items-center gap-x-1 text-sm">
                          <PriorityIcon />
                          Prioriterat
                        </div>
                      )}
                      {!!errorTriangle && (
                        <div className="ml-2">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                        </div>
                      )}
                    </DialogTitle>

                    <div className="w-full border-t border-gray-200" />

                    <div
                      className="px-2 sm:px-4 sm:pb-4"
                      // !!onDeleteClick && "pt-4",
                    >
                      {children}
                    </div>
                  </div>
                </div>

                {!hideButtons && (
                  <>
                    <div className="mt-4 w-full border-t border-gray-200 sm:mt-0" />
                    <div className="my-4 flex justify-end">
                      <div className="flex w-96 gap-x-2">
                        <ModalSecondaryAction
                          onClick={onClose}
                          text={closeButtonText}
                        />
                        {!!actionHandler && (
                          <ModalPrimaryButton
                            isLoading={isLoading}
                            buttonText={actionText || title}
                            loadingText={loadingText}
                            type="button"
                            onClick={actionHandler}
                          />
                        )}
                      </div>
                    </div>
                    {/* <div className="div mt-8 flex items-center justify-end bg-white px-2 sm:mt-0 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 sm:p-4 sm:px-0">
                      {!!actionHandler && (
                        <button
                          type="button"
                          className="inline-flex w-full justify-center rounded-md border border-transparent bg-cyan-700 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 disabled:bg-gray-400 sm:col-start-2 sm:text-sm"
                          onClick={actionHandler}
                          disabled={actionDisabled || isLoading}
                        >
                          {isLoading && (
                            <svg
                              className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          )}

                          {isLoading
                            ? !!loadingText
                              ? loadingText
                              : actionText || title
                            : actionText || title}
                        </button>
                      )}
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                        onClick={onClose}
                      >
                        {closeButtonText}
                      </button>
                    </div> */}
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;

type ModalCloseIconProps = {
  onClick: () => void;
};
export const ModalCloseIcon: FC<ModalCloseIconProps> = ({ onClick }) => (
  <div className="absolute right-0 top-0 block pr-4 pt-4 sm:pt-6">
    <button
      type="button"
      className="focus:ring-flowerpink-500 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
      onClick={onClick}
      tabIndex={-1}
    >
      <span className="sr-only">Close</span>
      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
    </button>
  </div>
);
